import { TrendingUp, Trophy, ArrowUpRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { getIconByName } from "@/components/team/performanceMetricsConfig";
import type { TeamPerformanceMetric } from "@/types/team";
import { cn } from "@/lib/utils";

interface MetricStat {
  metric: TeamPerformanceMetric;
  count: number;
  points: number;
}

interface PerformanceCardProps {
  currentMonth: string;
  metricStats: MetricStat[];
  totalCount: number;
  totalPoints: number;
}

export const PerformanceCard = ({ currentMonth, metricStats, totalCount, totalPoints }: PerformanceCardProps) => {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-card via-card to-primary/5">
      <CardHeader className="pb-2">
        <div 
          className="flex items-center gap-3 cursor-pointer group"
          onClick={() => navigate('/performance')}
        >
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-lg">
            <TrendingUp className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              Leistung {currentMonth}
              <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
            </CardTitle>
            <CardDescription>Ihre Performance im Überblick</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-4">
        {metricStats.length > 0 ? (
          <>
            {/* Total summary - clickable */}
            <div 
              className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent mb-4 cursor-pointer hover:from-primary/15 hover:via-primary/10 transition-colors group"
              onClick={() => navigate('/performance')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary">
                  <Trophy className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Gesamtpunkte</p>
                  <p className="text-2xl font-bold">{totalPoints}</p>
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
            </div>
            
            {/* Metrics grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {metricStats.map(({ metric, count, points }) => {
                const MetricIcon = getIconByName(metric.icon || 'trophy');
                return (
                  <div 
                    key={metric.id} 
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border bg-card/50 transition-all",
                      "hover:shadow-md hover:border-primary/20"
                    )}
                  >
                    <div className="p-2 rounded-lg bg-primary/10">
                      <MetricIcon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-muted-foreground truncate">{metric.name}</p>
                      <div className="flex items-baseline gap-1">
                        <p className="text-xl font-bold">{count}</p>
                        <span className="text-xs text-muted-foreground">({points} Pkt.)</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div 
              className="p-4 rounded-full bg-muted/50 w-fit mx-auto mb-3 cursor-pointer hover:bg-muted transition-colors"
              onClick={() => navigate('/performance')}
            >
              <Trophy className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mb-3">Keine Metriken definiert</p>
            <Link to="/performance">
              <Button variant="outline" size="sm" className="gap-2">
                Zur Leistungserfassung
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
